'use client';
import { useRouteContext } from '../../hooks/useRouteContext';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getSubmissionById, deleteSubmission, updateSubmission } from '../../redux/actions/submissionActions';
import fileService from '../../services/fileService';
import FileViewer from '../../components/viewers/FileViewer';
import { formatDateFR } from '../../utils/dateFormatter';

const SubmissionDetails = () => {
  const { getContextualPath } = useRouteContext();

  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { submission, loading, error } = useSelector(state => state.submissions);
  const { user } = useSelector(state => state.auth);

  const [activeFileId, setActiveFileId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingIndividual, setDownloadingIndividual] = useState(false);
  const [zipProgress, setZipProgress] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionReason, setActionReason] = useState('');

  const handleBackToList = () => navigate(getContextualPath('submissions'));

  const handleOpenAction = (action) => {
    setActionType(action);
    setActionReason('');
    setShowActionModal(true);
  };

  const handleSubmissionAction = async () => {
    try {
      await dispatch(updateSubmission(submission.id, {
        status: actionType === 'approve' ? 'approved' : 'rejected',
        adminNotes: actionReason
      }));
      setShowActionModal(false);
      dispatch(getSubmissionById(id));
    } catch (error) {
      console.error('Erreur lors de l\'action sur la soumission:', error);
    }
  };

  useEffect(() => {
    dispatch(getSubmissionById(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (submission?.files?.length > 0 && !activeFileId) {
      setActiveFileId(submission.files[0].id);
    }
  }, [submission, activeFileId]);

  const handleDownloadAll = async () => {
    try {
      setDownloadingAll(true);
      setZipProgress({ loaded: 0 });
      await fileService.downloadSubmissionFiles(submission.id, submission.name, (loaded) => {
        setZipProgress({ loaded });
      });
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
    } finally {
      setDownloadingAll(false);
      setZipProgress(null);
    }
  };

  const handleDownloadAllIndividual = async () => {
    try {
      setDownloadingIndividual(true);
      await fileService.downloadAllIndividually(submission.files);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
    } finally {
      setDownloadingIndividual(false);
    }
  };

  const handleDownload = async (fileId, filename) => {
    if (!fileId) return;
    try {
      setDownloading(fileId);
      const { blob, filename: serverFilename } = await fileService.downloadFile(fileId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = serverFilename || filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteSubmission(id));
      navigate(getContextualPath('submissions'));
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  const formatStatus = (status) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'approved': return 'Approuvé';
      case 'rejected': return 'Rejeté';
      case 'in_progress': return 'En cours';
      default: return status;
    }
  };

  const formatOrderStatus = (status) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'accepted': return 'Accepté';
      case 'in_progress': return 'En cours';
      case 'completed': return 'Terminé';
      case 'cancelled': return 'Annulé';
      case 'sent': return 'Expédié';
      default: return status;
    }
  };

  const getFileIcon = (filename) => {
    if (!filename) return 'insert_drive_file';
    const ext = filename.split('.').pop().toLowerCase();
    switch (ext) {
      case 'stl':
      case 'ply': return 'view_in_ar';
      case 'cam': return 'precision_manufacturing';
      case 'pdf': return 'picture_as_pdf';
      case 'dcm': return 'medical_services';
      default: return 'insert_drive_file';
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des détails...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="alert alert-danger">{error}</div>
        <button className="btn btn-primary" onClick={handleBackToList}>Retour à la liste</button>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="not-found-container">
        <i className="material-icons">error</i>
        <h2>Soumission non trouvée</h2>
        <p>La soumission que vous recherchez n'existe pas ou a été supprimée.</p>
        <button className="btn btn-primary" onClick={handleBackToList}>Retour à la liste</button>
      </div>
    );
  }

  const isAdmin = user?.role === 'admin';
  const canEdit = submission.status === 'pending' || isAdmin;
  const activeFile = submission.files?.find(f => f.id === activeFileId);

  return (
    <div className="submission-details-container">

      {/* ── Header ── */}
      <div className="page-header">
        <div className="header-left">
          <button className="btn-icon" onClick={handleBackToList}>
            <i className="material-icons">arrow_back</i>
          </button>
          <h1 className="sd-page-title">
            {submission.name}
            {submission.user && (
              <span className="sd-page-title__user"> — {submission.user.firstName} {submission.user.lastName}</span>
            )}
            <span className="sd-page-title__id"> #{submission.id}</span>
          </h1>
        </div>
        <div className="header-actions">
          {canEdit && (
            <Link to={getContextualPath(`submissions/${submission.id}/edit`)} className="btn btn-outline-primary">
              <i className="material-icons">edit</i>
              Modifier
            </Link>
          )}
          <button className="btn btn-outline-danger" onClick={() => setShowDeleteModal(true)}>
            <i className="material-icons">delete</i>
            Supprimer
          </button>
        </div>
      </div>

      {/* ── Meta bar ── */}
      <div className="sd-meta-bar">
        <span className={`status-badge ${submission.status}`}>{formatStatus(submission.status)}</span>
        <span className="sd-meta-bar__date">
          Soumis le {formatDateFR(submission.submissionDate)}
        </span>
        <span className="sd-meta-bar__id"># ID : {submission.id}</span>
      </div>

      {/* ── Admin actions ── */}
      {isAdmin && submission.status === 'pending' && (
        <div className="sd-admin-bar">
          <span className="sd-admin-bar__label">Actions administrateur</span>
          <div className="sd-admin-bar__actions">
            <button className="btn btn-success" onClick={() => handleOpenAction('approve')}>
              <i className="material-icons">check</i>
              Approuver la soumission
            </button>
            <button className="btn btn-danger" onClick={() => handleOpenAction('reject')}>
              <i className="material-icons">close</i>
              Rejeter la soumission
            </button>
          </div>
        </div>
      )}

      {/* ── Main layout ── */}
      <div className="sd-layout">

        {/* Left sidebar — Informations */}
        <div className="sd-sidebar">

          <div className="sd-card">
            <div className="sd-card__title">Informations</div>

            <div className="sd-field">
              <div className="sd-label">Titre</div>
              <div className="sd-value">{submission.name}</div>
            </div>

            <div className="sd-field">
              <div className="sd-label">Description</div>
              <div className="sd-value sd-value--muted">
                {submission.description || 'Aucune description'}
              </div>
            </div>

            {submission.user && (
              <div className="sd-field">
                <div className="sd-label">Soumis par</div>
                <div className="sd-value sd-value--strong">
                  {submission.user.firstName} {submission.user.lastName}
                </div>
              </div>
            )}

            <div className="sd-field">
              <div className="sd-label">Date</div>
              <div className="sd-value">{formatDateFR(submission.submissionDate)}</div>
            </div>

            {submission.order && (
              <div className="sd-field">
                <div className="sd-label">Commande</div>
                <div className="sd-order-chip">
                  <span className={`status-badge ${submission.order.status}`}>
                    {formatOrderStatus(submission.order.status)}
                  </span>
                  <Link
                    to={getContextualPath(`orders/${submission.order.id}`)}
                    className="sd-order-link"
                  >
                    Commande #{submission.order.id}
                    <i className="material-icons">arrow_forward</i>
                  </Link>
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Middle column — Fichiers */}
        <div className="sd-middle">
          <div className="sd-card">
            <div className="sd-card__title">
              Fichiers
              <span className="sd-badge">
                {submission.files?.length || 0} fichier{(submission.files?.length || 0) > 1 ? 's' : ''}
              </span>
            </div>

            {submission.files?.length > 0 ? (
              <>
                <div className="sd-file-list">
                  {submission.files.map(file => (
                    <div
                      key={file.id}
                      className={`sd-file-item ${file.id === activeFileId ? 'active' : ''}`}
                      onClick={() => setActiveFileId(file.id)}
                    >
                      <div className="sd-file-icon">
                        <i className="material-icons">{getFileIcon(file.filename)}</i>
                      </div>
                      <div className="sd-file-info">
                        <div className="sd-file-name">{file.filename}</div>
                        <div className="sd-file-size">{(file.size / (1024 * 1024)).toFixed(2)} MB</div>
                      </div>
                      <button
                        className="sd-file-dl-btn"
                        title="Télécharger"
                        onClick={(e) => { e.stopPropagation(); handleDownload(file.id, file.filename); }}
                        disabled={downloading === file.id}
                      >
                        <i className="material-icons">download</i>
                      </button>
                    </div>
                  ))}
                </div>

                {submission.files.length > 1 && (
                  <div className="sd-download-actions">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleDownloadAll}
                      disabled={downloadingAll || downloadingIndividual}
                    >
                      <i className="material-icons">folder_zip</i>
                      {downloadingAll ? 'Préparation...' : 'Télécharger ZIP'}
                    </button>
                    {/* bouton "Tout télécharger" masqué — fonctionnalité conservée */}
                  </div>
                )}
              </>
            ) : (
              <p className="sd-empty">Aucun fichier</p>
            )}
          </div>
        </div>

        {/* Right — Viewer */}
        <div className="sd-viewer">
          <div className="sd-viewer-card">
            <div className="sd-viewer-header">
              <span className="sd-viewer-title">
                {activeFile
                  ? (['stl', 'ply', 'cam'].includes(activeFile.filename.split('.').pop().toLowerCase()) ? 'Aperçu 3D' :
                     activeFile.filename.split('.').pop().toLowerCase() === 'pdf' ? 'Aperçu PDF' : 'Aperçu')
                  : 'Aperçu'}
              </span>
              {activeFile && (
                <span className="sd-viewer-filename">{activeFile.filename}</span>
              )}
            </div>
            <div className="sd-viewer-body">
              {activeFile ? (
                <FileViewer fileId={activeFileId} filename={activeFile.filename} />
              ) : (
                <div className="sd-viewer-empty">
                  <i className="material-icons">view_in_ar</i>
                  <p>Sélectionnez un fichier</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Modal action admin ── */}
      {showActionModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>{actionType === 'approve' ? 'Approuver la soumission' : 'Rejeter la soumission'}</h3>
              <button className="modal-close" onClick={() => setShowActionModal(false)}>
                <i className="material-icons">close</i>
              </button>
            </div>
            <div className="modal-body">
              <p>
                {actionType === 'approve'
                  ? 'Êtes-vous sûr de vouloir approuver cette soumission ?'
                  : 'Êtes-vous sûr de vouloir rejeter cette soumission ?'}
              </p>
              <p><strong>Soumission :</strong> {submission.name}</p>
              <div className="form-group">
                <label htmlFor="actionReason">
                  {actionType === 'approve' ? 'Notes (optionnel)' : 'Raison du rejet'}
                </label>
                <textarea
                  id="actionReason"
                  className="form-control"
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  rows="3"
                  placeholder={actionType === 'approve'
                    ? 'Notes supplémentaires pour l\'utilisateur'
                    : 'Raison du rejet de la soumission'}
                  required={actionType === 'reject'}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={() => setShowActionModal(false)}>
                Annuler
              </button>
              <button
                className={`btn ${actionType === 'approve' ? 'btn-success' : 'btn-danger'}`}
                onClick={handleSubmissionAction}
                disabled={actionType === 'reject' && !actionReason.trim()}
              >
                {actionType === 'approve' ? 'Approuver' : 'Rejeter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ZIP progress toast ── */}
      {zipProgress && (
        <div className="zip-progress-toast">
          <div className="zip-progress-toast__header">
            <i className="material-icons">folder_zip</i>
            <span>Préparation du ZIP…</span>
          </div>
          <div className="zip-progress-bar zip-progress-bar--indeterminate">
            <div className="zip-progress-bar__fill zip-progress-bar__fill--indeterminate" />
          </div>
          <div className="zip-progress-toast__info">
            {(zipProgress.loaded / (1024 * 1024)).toFixed(1)} Mo reçus…
          </div>
        </div>
      )}

      {/* ── Modal suppression ── */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>Confirmer la suppression</h3>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                <i className="material-icons">close</i>
              </button>
            </div>
            <div className="modal-body">
              <p>Êtes-vous sûr de vouloir supprimer cette soumission ?</p>
              <p className="text-danger">Cette action est irréversible.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={() => setShowDeleteModal(false)}>
                Annuler
              </button>
              <button className="btn btn-danger" onClick={handleDelete}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmissionDetails;
